import React from 'react'
import { Menu, Grid, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import FilterWrapper from 'components/Shared/FilterBuilder/FilterWrapper'
import FilterMenus from 'components/Participants/FilterBuilder/FilterMenus'
import { Components } from 'components/Participants/Filters'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import {
  resetFilters,
  toggleHasFilterValueStatus,
} from 'redux/slices/FilterBuilder'
import { FB_FILTER_LIST as filters } from 'lib/participants/Constants'
import AddFilterButton from 'components/Shared/FilterBuilder/AddFilterButton'
import ClearAllFiltersButton from 'components/Shared/FilterBuilder/ClearAllFiltersButton'
import FilterByLastSeen from 'components/Participants/Filters/FilterByLastSeen'
import FilterByJoinDate from 'components/Participants/Filters/FilterByJoinDate'
import FilterByEmail from 'components/Participants/Filters/Email'
import FilterByScreenName from 'components/Participants/Filters/ScreenName'
import SignUpQuestionPills from 'components/Participants/Filters/SignUpQuestions/FilterPills'
import SignUpQuestions from 'components/Participants/Filters/SignUpQuestions'
import {
  CreateGroupButton,
  FilterGroupListButton,
} from 'components/Participants/Group'
import FilterByProject from 'components/Participants/Filters/Project'
import { isAuthorized } from 'lib/authorization/AuthorizeComponent'

type FilterBuilderProps = {
  onClearFilter: () => void
}

export type FilterItem = {
  name?: string
  divider?: boolean
  disabled?: boolean
  FilterItem?: boolean
  icon?: string
  component?: string | null
  tooltipText?: string
  isDefault?: boolean
}

const filterWithComponent = (filterProps: any, index: number) => {
  const { component } = filterProps
  const Filter = Components[component]

  return (
    Filter && (
      <Filter type={filterProps.name} item={filterProps} index={index} />
    )
  )
}

const FilterBuilder = (props: FilterBuilderProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const theme: AdminUITheme = useTheme()
  const dispatch = useAppDispatch()

  const showLocationFilter = isAuthorized('location_question')

  const filtersWithRemovedItem = (value: string) =>
    filters.filter((filter) => filter.name !== value)

  const prmFilters = showLocationFilter
    ? filtersWithRemovedItem('suburbs')
    : filtersWithRemovedItem('location')

  const { onClearFilter } = props
  const selectedFilters = useAppSelector(
    (state) => state.filterBuilder.selectedFilters,
  )
  const selectedQuestions = useAppSelector(
    (state) => state.signUpQuestions.selectedQuestions,
  )

  const handleAddFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClearFilter = () => {
    dispatch(toggleHasFilterValueStatus(true))
    dispatch(resetFilters())
    onClearFilter()
  }

  const showSelectedFiltersOnUI = () => {
    return selectedFilters.map((filter: any, index: number) => {
      if (!filter.component) return

      return (
        <Grid
          item
          key={index}
          style={theme.ParticipantGrid.selectedFilterWidth}
        >
          {filterWithComponent(filter, index)}
        </Grid>
      )
    })
  }

  const isClearFilterButtonDisabled = () => {
    return selectedFilters.length === 0 && selectedQuestions.length === 0
  }

  const filterMenuProps = { handleClose }

  return (
    // making the css as important as MUI default grid item css is creating obligation for desired style
    <Grid
      item
      xs={12}
      sx={{
        paddingTop: '1rem !important',
      }}
    >
      <FilterWrapper>
        <>
          <Grid item>
            <FilterGroupListButton />
          </Grid>
          {showSelectedFiltersOnUI()}
          <SignUpQuestionPills />
          <Grid item xs={12} sm={12} md={8} lg={6}>
            <Box display="flex" alignSelf="flex-start" gap={1}>
              <AddFilterButton handleAddFilterClick={handleAddFilterClick} />
              <CreateGroupButton />
              <ClearAllFiltersButton
                handleClearFilter={handleClearFilter}
                isDisabled={isClearFilterButtonDisabled()}
              />
            </Box>

            <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
              <FilterMenus filters={prmFilters} {...filterMenuProps} />
            </Menu>
          </Grid>
        </>
      </FilterWrapper>

      <SignUpQuestions />
      <FilterByEmail />
      <FilterByScreenName />
      <FilterByLastSeen />
      <FilterByJoinDate />
      <FilterByProject />
    </Grid>
  )
}

export default FilterBuilder
